import type { MainToWorker } from './protocol';
import type { WorkCategory } from './workHealth';

export type WorkRequirement =
	| 'mutation'
	| 'graph'
	| 'editor'
	| 'tray'
	| 'material'
	| 'preview'
	| 'result'
	| 'load';
export type WorkContract = {
	category: WorkCategory;
	requirement: WorkRequirement;
	expectedRejection?: boolean;
};
const mutation: WorkContract = { category: 'graph', requirement: 'mutation' };
const parameter: WorkContract = { category: 'parameter', requirement: 'mutation' };
const editor: WorkContract = { category: 'editor', requirement: 'editor' };
const load: WorkContract = { category: 'load', requirement: 'load' };

/** Explicit inventory, shared by page and worker. No method-name heuristics and
 * no built-in node special cases. Arguments never enter diagnostic payloads. */
export const COMMAND_WORK: Readonly<Record<string, WorkContract>> = {
	edit: mutation,
	editSystem: mutation,
	addNode: mutation,
	removeNode: mutation,
	removeNodes: mutation,
	setValue: parameter,
	setValueLocal: parameter,
	setValueSystem: parameter,
	setParamsBatch: parameter,
	setParamsBatchSilent: { category: 'parameter', requirement: 'graph' },
	undo: mutation,
	redo: mutation,
	loadGraphToml: load,
	loadGraphJson: load,
	editorLibraryDrag: editor,
	editorBeginNodePlacement: editor,
	editorCompleteNodePicker: mutation,
	editorLibraryDrop: mutation,
	editorCompleteConnectionNode: mutation,
	editorCancelConnectionNode: editor,
	editorSelectOutput: editor,
	editorSelectNode: editor,
	editorCancelNote: editor,
	editorSelectNote: editor,
	editorCommitNote: mutation,
	editorPortalAction: mutation,
	editorGraphChanged: editor,
	setOutputTrayLayout: { category: 'editor', requirement: 'tray' },
	resetTime: { category: 'graph', requirement: 'graph' },
	setMaterialViewportEnvironmentQuality: { category: 'material_viewport', requirement: 'material' }
};
const unobservedQueries: ReadonlySet<string> = new Set([
	'timeDebug',
	'editorDebugState',
	'assetCacheStats',
	'readMaterialViewportTiming',
	'contentJson',
	'computeLayout',
	'aqueduct_material_role_contract_json',
	'listRegistry',
	'aqueduct_sanitize_graph_name',
	'safeExportFilenameComponent',
	'resolveExportTextureFilenames',
	'aqueduct_compiler_free',
	'readGraphProfile',
	'readVramStats',
	'readBlitTiming',
	'readMaterialViewportGpuTiming',
	'dumpProfile',
	'graphProfilingSupported',
	'gpuTimestampsSupported',
	'timeNodePasses'
]);
const exports: ReadonlySet<string> = new Set([
	'beginTextureExport',
	'textureExportStep',
	'exportOutputsZip',
	'exportNodePng',
	'exportOutputPng',
	'exportMaterialThumbnailPair',
	'exportTextures',
	'exportTexture',
	'exportToml',
	'exportOutput',
	'exportMaterial',
	'renderAtTimeAndWait'
]);
export function workContract(message: MainToWorker): WorkContract | undefined {
	switch (message.op) {
		case 'openProject':
			return load;
		case 'acquireProject':
			return { category: 'boot', requirement: 'result' };
		case 'cmd':
			return COMMAND_WORK[message.method];
		case 'query':
		case 'moduleQuery':
			if (unobservedQueries.has(message.method)) return undefined;
			if (message.method === 'prepareGraphPipelines')
				return { category: 'preparation', requirement: 'result' };
			if (message.method === 'loadGraphToml' || message.method === 'loadGraphJson') return load;
			if (
				message.method === 'aqueduct_compile_shader' ||
				message.method === 'validateNodeDefinition' ||
				message.method === 'validateArtifactShader'
			)
				return { category: 'compile', requirement: 'result', expectedRejection: true };
			if (message.method === 'normalizeAsset' || message.method === 'imageAssetPreview')
				return { category: 'asset', requirement: 'result', expectedRejection: true };
			return { category: exports.has(message.method) ? 'export' : 'query', requirement: 'result' };
		case 'surfaceScreenshot':
			return { category: 'export', requirement: 'result' };
		case 'setUserResolution':
			return { category: 'resolution', requirement: 'mutation' };
		case 'scheduleRender':
		case 'renderFullDocument':
			return { category: 'graph', requirement: 'graph' };
		case 'editorPointer':
		case 'outputTrayPointer':
			// Move without a pressed button is passive hover. Phase 3 cancels a gesture.
			if (message.ev.phase === 1 && (message.ev.buttons ?? (message.ev.button < 0 ? 0 : 1)) === 0)
				return undefined;
			return {
				category: 'editor',
				requirement: message.op === 'editorPointer' ? 'editor' : 'tray'
			};
		case 'editorNavigate':
		case 'editorWheel':
		case 'editorKey':
			return editor;
		case 'mvRender':
		case 'mvSetSourceMode':
		case 'mvSetExternalTexture':
		case 'mvClearExternalTexture':
			return { category: 'material_viewport', requirement: 'material' };
		case 'previewBlit':
		case 'previewSetSource':
			return { category: 'preview', requirement: 'preview' };
		default:
			return undefined;
	}
}

/** Stable typed outcome emitted by the WASM render boundary. */
export function isExpectedWorkError(error: unknown): boolean {
	return error instanceof Error && error.name === 'AqueductValidationError';
}
